import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../../utils/authService';

/**
 * Empresa activa: el contexto que comparten las pantallas de operación.
 *
 * ── El problema que resuelve ────────────────────────────────────────────────
 * Antes la empresa viajaba SOLO dentro del `state` de la navegación: funcionaba
 * si llegabas por el botón "Ver empresa", y se perdía en cuanto entrabas por el
 * menú lateral —ahí cada pantalla caía en "la primera empresa del perfil"—. Peor
 * aún, el Panel y la Sincronización SAT resolvían la empresa con reglas
 * distintas, así que podían acabar mostrando empresas diferentes.
 *
 * ── Cómo funciona ahora ─────────────────────────────────────────────────────
 * La empresa activa se decide una vez, aquí, con este orden:
 *   1. `?companyId=` de la URL  → manda siempre: hace que un enlace se pueda
 *      compartir y que F5 no cambie de empresa.
 *   2. La última elegida en este navegador.
 *   3. La primera del perfil.
 *
 * Al cambiarla se escribe en los tres sitios (estado, almacenamiento y URL), así
 * que la barra de direcciones siempre dice qué se está viendo.
 *
 * Solo aplica a quien tiene más de una empresa (el super admin, y un
 * administrador con varias asignadas). Para el resto es transparente.
 */

const LLAVE = 'payslip-empresa-activa';

/** Rutas cuyo contenido depende de la empresa activa. */
export const RUTAS_DE_EMPRESA = ['/app/company-dashboard', '/app/payroll-processing', '/app/receipts'];

export const esRutaDeEmpresa = (pathname) => RUTAS_DE_EMPRESA.includes(pathname);

const CompanyCtx = createContext({
  empresas: [],
  empresaActiva: null,
  cargando: false,
  setEmpresaActiva: () => {},
  sincronizacionEnCurso: null,
  setSincronizacionEnCurso: () => {},
});

const leerGuardada = () => {
  try {
    const v = localStorage.getItem(LLAVE);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
};

const guardar = (id) => {
  try {
    if (id == null) localStorage.removeItem(LLAVE);
    else localStorage.setItem(LLAVE, String(id));
  } catch {
    /* almacenamiento no disponible: la elección vive solo en memoria */
  }
};

export const CompanyProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [empresas, setEmpresas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [idActiva, setIdActiva] = useState(() => {
    const deUrl = searchParams.get('companyId');
    return deUrl ? Number(deUrl) : leerGuardada();
  });

  const rol = authService?.getCurrentUser()?.role;
  // El colaborador no elige empresa: ve la suya y punto.
  const aplica = rol === 'administrator' || rol === 'company';

  // Catálogo de empresas del perfil. Se pide UNA vez por sesión de shell.
  useEffect(() => {
    if (!aplica) return undefined;

    let cancelado = false;
    (async () => {
      try {
        setCargando(true);
        const perfil = await authService?.getUserProfile();
        if (cancelado) return;
        setEmpresas(Array.isArray(perfil?.companies) ? perfil.companies : []);
      } catch (e) {
        console.error('Error cargando las empresas del perfil:', e);
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();

    return () => { cancelado = true; };
  }, [aplica]);

  // Si la URL trae companyId, manda ella (enlace compartido, recarga, atrás).
  useEffect(() => {
    const deUrl = searchParams.get('companyId');
    if (!deUrl) return;
    const n = Number(deUrl);
    if (!Number.isNaN(n) && n !== idActiva) {
      setIdActiva(n);
      guardar(n);
    }
  }, [searchParams, idActiva]);

  // Sin elección previa (o apuntando a una empresa que ya no existe), se toma la
  // primera del perfil para no dejar la pantalla sin datos.
  useEffect(() => {
    if (!empresas.length) return;
    const sigueExistiendo = empresas.some((c) => Number(c?.id) === Number(idActiva));
    if (!sigueExistiendo) {
      const primera = Number(empresas[0]?.id);
      setIdActiva(primera);
      guardar(primera);
    }
  }, [empresas, idActiva]);

  // En una pantalla de empresa la URL SIEMPRE dice cuál se está viendo, aunque
  // hayas llegado por el menú lateral. Así el enlace se puede compartir y F5 no
  // cambia de empresa. Se reemplaza la entrada del historial: no es un paso
  // nuevo de navegación, es el mismo sitio mejor descrito.
  useEffect(() => {
    if (!aplica || idActiva == null) return;
    if (!esRutaDeEmpresa(location.pathname)) return;
    if (searchParams.get('companyId')) return;

    const params = new URLSearchParams(location.search);
    params.set('companyId', String(idActiva));
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  }, [aplica, idActiva, location.pathname, location.search, searchParams, navigate]);

  const setEmpresaActiva = useCallback((id) => {
    const n = Number(id);
    setIdActiva(n);
    guardar(n);

    // La URL refleja la empresa en las pantallas que dependen de ella.
    if (esRutaDeEmpresa(location.pathname)) {
      const params = new URLSearchParams(location.search);
      params.set('companyId', String(n));
      navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const empresaActiva = useMemo(
    () => empresas.find((c) => Number(c?.id) === Number(idActiva)) || null,
    [empresas, idActiva],
  );

  // La pantalla de sincronización SAT lo publica mientras hay una descarga en
  // curso ({ idEmpresa, nombre }); el selector de empresa lo lee para avisar
  // antes de cambiar. Fuera de esa pantalla siempre es null.
  const [sincronizacionEnCurso, setSincronizacionEnCurso] = useState(null);

  const valor = useMemo(
    () => ({
      empresas,
      empresaActiva,
      idEmpresaActiva: idActiva,
      cargando,
      setEmpresaActiva,
      sincronizacionEnCurso,
      setSincronizacionEnCurso,
    }),
    [empresas, empresaActiva, idActiva, cargando, setEmpresaActiva, sincronizacionEnCurso],
  );

  return <CompanyCtx.Provider value={valor}>{children}</CompanyCtx.Provider>;
};

/**
 * `{ empresas, empresaActiva, idEmpresaActiva, cargando, setEmpresaActiva,
 *    sincronizacionEnCurso, setSincronizacionEnCurso }`
 */
export const useCompany = () => useContext(CompanyCtx);

export default CompanyCtx;
